<template>
  <a-card title="场景模板列表">
    <a-button type="primary" @click="goBack">返回场景列表</a-button>
    
    <a-card v-if="sceneData" size="small" style="margin-top: 10px; margin-bottom: 10px;" :title="'场景信息'">
      <div class="scene-info">
        <div class="info-item">
          <span class="label">场景标题:</span>
          <span class="value">{{ sceneData.title }}</span>
        </div>
        <div class="info-item">
          <span class="label">状态:</span>
          <span class="value">{{ getStatusText(sceneData.status) }}</span>
        </div>
        <div class="info-item">
          <span class="label">地区:</span>
          <span class="value">{{ sceneData.region_code || '-' }}</span>
        </div>
        <div class="info-item">
          <span class="label">排序:</span>
          <span class="value">{{ sceneData.sorted }}</span>
        </div>
        <div class="info-item">
          <span class="label">来源:</span>
          <span class="value">{{ sceneData.source || '-' }}</span>
        </div>
        <div class="info-item">
          <span class="label">模板数量:</span>
          <span class="value">{{ templateIds.length }}</span>
        </div>
      </div>
      <div class="add-templates-section">
        <h4>添加模板</h4>
        <a-input
          v-model:value="newTemplateIds"
          placeholder="请输入逗号分隔的模板ID，例如：1,2,3"
          style="width: 400px; margin-right: 10px;"
        />
        <a-button type="primary" @click="addTemplates" :loading="addingTemplates">
          添加模板
        </a-button>
      </div>
    </a-card>
    
    <a-alert :message="noticeMessage" type="info" show-icon style="text-align: left; margin-top: 10px;" />
    <a-table
      class="ant-table-striped"
      :row-key="record => record.id"
      :data-source="dataSource"
      :columns="columns"
      :pagination="pagination"
      :loading="loading"
      :row-class-name="(_record, index) => (index % 2 === 1 ? 'table-striped' : null)"
    >
      <template #image_url="{ record }">
        <a-image
          style="width: 100px;"
          :src="thumbnail(record.image_url)"
          width="100"
        />
      </template>
      <template #region_code="{ record }">
        <RegionCode :region_code="record.region_code" />
      </template>
      <template #action="{ record }">
        <a-space>
          <a-popconfirm
            title="确定从场景中移除该模板吗?"
            ok-text="确定"
            cancel-text="取消"
            @confirm="handleDeleteTemplate(record)"
          >
            <a-button danger>删除</a-button>
          </a-popconfirm>
        </a-space>
      </template>
    </a-table>
  </a-card>
</template>

<script>
import { defineComponent, onMounted, ref } from 'vue'
import { message } from 'ant-design-vue'
import request from '@/utils/request'
import { thumbnail, rawImage } from '@/utils/image'
import RegionCode from '@/components/RegionCode.vue'

export default defineComponent({
  props: {
    sceneId: {
      type: String,
      required: true
    }
  },
  components: {
    RegionCode
  },
  setup(props) {
    const loading = ref(false)
    const dataSource = ref([])
    const pagination = ref({
      total: 1,
      current: 1,
      pageSize: 20,
      'show-size-changer': true,
      pageSizeOptions: ['20', '50', '100']
    })
    const noticeMessage = ref('')
    const sceneData = ref(null)
    const templateIds = ref([])
    const newTemplateIds = ref('')
    const addingTemplates = ref(false)

    const goBack = () => {
      window.history.back()
    }

    const fetchSceneData = async () => {
      loading.value = true
      try {
        const rsp = await request.get(`/api/template_scenes/${props.sceneId}`)
        if (rsp && rsp.data) {
          sceneData.value = rsp.data
          templateIds.value = sceneData.value.template_ids ? sceneData.value.template_ids.split(',').filter(id => id) : []
          fetchTemplates()
        }
      } catch (error) {
        console.error('获取场景数据失败:', error)
        message.error('获取场景数据失败')
      } finally {
        loading.value = false
      }
    }

    const fetchTemplates = async () => {
      if (templateIds.value.length === 0) {
        dataSource.value = []
        noticeMessage.value = '共 0 条'
        pagination.value.total = 0
        return
      }

      loading.value = true
      try {
        // 这里需要根据实际的模板API来获取模板列表
        // 假设模板API为 /templates/batch_get，支持传入ids参数
        const rsp = await request.get(`/api/template_scenes/${props.sceneId}/templates`, { })

        if (rsp && rsp.data) {
          dataSource.value = rsp.data.templates || []
          noticeMessage.value = `共 ${dataSource.value.length} 条`
          pagination.value.total = dataSource.value.length
        } else {
          dataSource.value = []
          noticeMessage.value = '共 0 条'
          pagination.value.total = 0
        }
      } catch (error) {
        console.error('获取模板数据失败:', error)
        dataSource.value = []
        noticeMessage.value = '共 0 条'
        pagination.value.total = 0
      } finally {
        loading.value = false
      }
    }

    const handleDeleteTemplate = async (record) => {
      loading.value = true
      try {
        // 从templateIds中移除当前模板ID
        const newTemplateIds = templateIds.value.filter(id => id !== record.id.toString())
        
        // 更新场景的template_ids
        await request.put(`/api/template_scenes/${props.sceneId}`, {
          template_ids: newTemplateIds.join(',')
        })

        message.success('删除成功')
        templateIds.value = newTemplateIds
        fetchTemplates()
      } catch (error) {
        console.error('删除模板失败:', error)
        message.error('删除失败')
      } finally {
        loading.value = false
      }
    }

    const addTemplates = async () => {
      if (!newTemplateIds.value.trim()) {
        message.warning('请输入模板ID')
        return
      }

      // 解析输入的模板IDs，去重并验证格式
      const inputIds = newTemplateIds.value
        .split(',')
        .map(id => id.trim())
        .filter(id => id && /^\d+$/.test(id))
      
      if (inputIds.length === 0) {
        message.warning('请输入有效的数字模板ID')
        return
      }

      // 合并到现有模板IDs中，去重
      const allIds = [...new Set([...templateIds.value, ...inputIds])]
      
      addingTemplates.value = true
      try {
        // 更新场景的template_ids
        await request.put(`/api/template_scenes/${props.sceneId}`, {
          template_ids: allIds.join(',')
        })

        message.success('添加成功')
        templateIds.value = allIds
        newTemplateIds.value = '' // 清空输入
        fetchTemplates()
      } catch (error) {
        console.error('添加模板失败:', error)
        message.error('添加失败')
      } finally {
        addingTemplates.value = false
      }
    }

    onMounted(() => {
      fetchSceneData()
    })

    const columns = [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
      },
      {
        title: '名称',
        dataIndex: 'title',
        key: 'title',
      },
      {
        title: '封面',
        dataIndex: 'image_url',
        key: 'image_url',
        slots: { customRender: 'image_url' },
      },
      {
        title: '工具类型',
        dataIndex: 'tool_id',
        key: 'tool_id',
      },
      {
        title: '任务类型',
        dataIndex: 'style_id',
        key: 'style_id',
      },
      {
        title: '地区',
        dataIndex: 'region_code',
        key: 'region_code',
        slots: { customRender: 'region_code' },
      },
      {
        title: '操作',
        dataIndex: 'action',
        key: 'action',
        slots: { customRender: 'action' },
      },
    ]

    const getStatusText = (status) => {
      const statusMap = {
        0: '下线',
        1: '测试环境',
        2: '上线'
      }
      return statusMap[status] || '未知'
    }

    return {
      loading,
      dataSource,
      pagination,
      noticeMessage,
      sceneData,
      templateIds,
      newTemplateIds,
      addingTemplates,
      columns,
      thumbnail,
      rawImage,
      goBack,
      handleDeleteTemplate,
      addTemplates,
      getStatusText
    }
  }
})
</script>

<style scoped>
.ant-table-striped :deep(.table-striped) td {
  background-color: #fafafa;
}

.scene-info {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 10px;
}

.info-item {
  display: flex;
  align-items: center;
}

.label {
  font-weight: 600;
  margin-right: 8px;
  min-width: 80px;
  color: #666;
}

.value {
  color: #333;
}

.add-templates-section {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #f0f0f0;
}

.add-templates-section h4 {
  margin-bottom: 10px;
  color: #333;
  font-size: 14px;
  font-weight: 600;
}
</style>